import { z } from 'zod';

/**
 * Contrats partagés du feedback utilisateur (pouce haut/bas + commentaire) sur les
 * réponses des agents conversationnels CD-v3 et Edition-v3.
 *
 * Persistance : table `message_feedback` (OVH), ancrée sur le dernier tour assistant
 * d'une réponse (`casediscovery_messages_v2.id`). Voir migration 111.
 */

/** Le vote binaire. */
export const MESSAGE_FEEDBACK_RATINGS = ['up', 'down'] as const;
export const MessageFeedbackRatingSchema = z.enum(MESSAGE_FEEDBACK_RATINGS);
export type MessageFeedbackRating = z.infer<typeof MessageFeedbackRatingSchema>;

/**
 * Catalogue des catégories d'erreur proposées sur un pouce-bas.
 *
 * SOURCE UNIQUE : la `key` est gravée à l'identique dans le CHECK SQL de
 * `message_feedback.reason` (migration 111). Toute évolution doit toucher les deux
 * côtés. Le `label` est le texte affiché à l'avocat dans le dépli (web + add-in).
 *
 * Ne s'applique JAMAIS à un pouce-haut (reason reste `null`). L'ordre fait foi pour
 * l'affichage des puces.
 */
export const MESSAGE_FEEDBACK_REASON_CATALOG = [
  { key: 'legal_inaccurate', label: 'Juridiquement inexact' },
  { key: 'fabricated_source', label: 'Référence inventée ou inexacte' },
  { key: 'missed_request', label: 'Non pertinent' },
  { key: 'superficial', label: 'Trop superficiel / incomplet' },
  { key: 'form', label: 'Forme inadaptée' },
  { key: 'other', label: 'Autre' },
] as const;

export type MessageFeedbackReason =
  (typeof MESSAGE_FEEDBACK_REASON_CATALOG)[number]['key'];

const REASON_KEYS = MESSAGE_FEEDBACK_REASON_CATALOG.map((r) => r.key) as [
  MessageFeedbackReason,
  ...MessageFeedbackReason[],
];
export const MessageFeedbackReasonSchema = z.enum(REASON_KEYS);

/** Borne du commentaire libre — identique au CHECK SQL `char_length(comment) <= 2000`. */
export const MESSAGE_FEEDBACK_COMMENT_MAX = 2000;

/**
 * Corps de `POST /api/case-discovery-v3/messages/:messageId/feedback` (upsert
 * create-or-replace ; POST et non PUT car l'httpClient de l'add-in n'expose pas put).
 *
 * `reason`/`comment` sont réservés au pouce-bas : un pouce-haut ne porte ni
 * catégorie ni commentaire (le refine le garantit, en miroir du CHECK
 * `message_feedback_reason_down_only`).
 */
export const SubmitMessageFeedbackSchema = z
  .object({
    rating: MessageFeedbackRatingSchema,
    reason: MessageFeedbackReasonSchema.nullish(),
    comment: z.string().max(MESSAGE_FEEDBACK_COMMENT_MAX).nullish(),
  })
  .refine(
    (v) =>
      v.rating === 'down' ||
      (v.reason == null && (v.comment == null || v.comment.trim() === '')),
    {
      message:
        'message_feedback: reason et comment sont réservés au rating=down (pouce-bas)',
    },
  );
export type SubmitMessageFeedback = z.infer<typeof SubmitMessageFeedbackSchema>;

/** Réponse de l'endpoint upsert — l'état courant du feedback pour ce (message, user). */
export const MessageFeedbackResponseSchema = z.object({
  id: z.string().uuid(),
  messageId: z.string().uuid(),
  conversationId: z.string().uuid(),
  rating: MessageFeedbackRatingSchema,
  reason: MessageFeedbackReasonSchema.nullable(),
  comment: z.string().nullable(),
  /** ISO 8601. */
  updatedAt: z.string(),
});
export type MessageFeedbackResponse = z.infer<typeof MessageFeedbackResponseSchema>;

/**
 * État d'un vote, tel que rechargé à l'ouverture d'une conversation, pour que
 * l'UI rallume le pouce déjà donné par l'utilisateur (et pré-remplisse le détail).
 */
export const MessageFeedbackStateSchema = z.object({
  messageId: z.string().uuid(),
  rating: MessageFeedbackRatingSchema,
  reason: MessageFeedbackReasonSchema.nullable(),
  comment: z.string().nullable(),
});
export type MessageFeedbackState = z.infer<typeof MessageFeedbackStateSchema>;

/** Réponse de `GET /…/conversations/:id/feedback` — les votes de l'utilisateur courant. */
export const ConversationFeedbackResponseSchema = z.array(MessageFeedbackStateSchema);
export type ConversationFeedbackResponse = z.infer<typeof ConversationFeedbackResponseSchema>;
